import React, { useState, useMemo, useCallback } from "react";
import {
  Container,
  Header,
  Box,
  SpaceBetween,
  StatusIndicator,
  Badge,
  Button,
  Table,
  Link,
  SegmentedControl,
} from "@cloudscape-design/components";
import { WorkflowDetailModal, TimestampDisplay } from "../common";
import { WorkflowExecution, WorkflowStatus } from "../../services";
import { DateFormatter, StatusMapper, AwsUrlBuilder } from "../../utils";
import { TimeDisplayMode } from "../../utils/DateFormatter";
import { useTimeDisplayMode } from "../../hooks";
import { LABELS } from "../../constants";

interface WorkflowPanelProps {
  workflows: WorkflowExecution[];
  loading: boolean;
  onRedrive: (executionArn: string) => void;
}

const StatusSummary: React.FC<{ workflows: WorkflowExecution[] }> = ({ workflows }) => {
  const counts = workflows.reduce<Record<string, number>>((acc, w) => {
    acc[w.status] = (acc[w.status] || 0) + 1;
    return acc;
  }, {});

  const getBadgeColor = (status: string): "green" | "red" | "blue" => {
    if (status === WorkflowStatus.SUCCEEDED) {
      return "green";
    }
    if (status === WorkflowStatus.FAILED) {
      return "red";
    }
    return "blue";
  };

  return (
    <SpaceBetween direction="horizontal" size="xs">
      {Object.entries(counts).map(([status, count]) => (
        <Badge key={status} color={getBadgeColor(status)}>
          {status}: {count}
        </Badge>
      ))}
    </SpaceBetween>
  );
};

const renderStatus = (item: WorkflowExecution): React.ReactNode => (
  <StatusIndicator type={StatusMapper.mapWorkflowStatus(item.status)}>{item.status}</StatusIndicator>
);

const renderDuration = (item: WorkflowExecution): string => DateFormatter.formatDuration(item.startDate, item.stopDate, LABELS.RUNNING);

export const WorkflowPanel: React.FC<WorkflowPanelProps> = ({ workflows, loading, onRedrive }) => {
  const [selectedWorkflow, setSelectedWorkflow] = useState<WorkflowExecution | null>(null);
  const [timeMode, setTimeMode] = useTimeDisplayMode();

  const sortedWorkflows = useMemo(() => [...workflows].sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()), [
    workflows,
  ]);

  const handleRedrive = useCallback(
    (arn: string): void => {
      setSelectedWorkflow(null);
      onRedrive(arn);
    },
    [onRedrive]
  );

  const renderName = useCallback(
    (item: WorkflowExecution): React.ReactNode => (
      <Link fontSize="body-m" onFollow={() => setSelectedWorkflow(item)}>
        {item.workflowName}
      </Link>
    ),
    []
  );

  const renderStarted = useCallback(
    (item: WorkflowExecution): React.ReactNode => <TimestampDisplay date={item.startDate} mode={timeMode} />,
    [timeMode]
  );

  const renderEnded = useCallback(
    (item: WorkflowExecution): React.ReactNode => <TimestampDisplay date={item.stopDate} mode={timeMode} fallback={LABELS.RUNNING} />,
    [timeMode]
  );

  const renderActions = useCallback(
    (item: WorkflowExecution): React.ReactNode => (
      <SpaceBetween direction="horizontal" size="xs">
        <Button variant="inline-link" onClick={() => setSelectedWorkflow(item)}>
          {LABELS.VIEW_DETAILS}
        </Button>
        <Link external href={AwsUrlBuilder.buildStepFunctionsUrl(item.executionArn)}>
          {LABELS.CONSOLE}
        </Link>
        {item.status === WorkflowStatus.FAILED && (
          <Button variant="inline-link" onClick={() => onRedrive(item.executionArn)}>
            {LABELS.REDRIVE}
          </Button>
        )}
      </SpaceBetween>
    ),
    [onRedrive]
  );

  return (
    <>
      <Container
        header={
          <Header
            variant="h2"
            counter={`(${workflows.length})`}
            description={<StatusSummary workflows={workflows} />}
            actions={
              <SegmentedControl
                selectedId={timeMode}
                onChange={({ detail }) => setTimeMode(detail.selectedId as TimeDisplayMode)}
                label={LABELS.TIME_DISPLAY}
                options={[
                  { id: "local", text: LABELS.LOCAL_TIME },
                  { id: "utc", text: LABELS.UTC_TIME },
                ]}
              />
            }
          >
            {LABELS.WORKFLOWS}
          </Header>
        }
      >
        <Table
          loading={loading}
          items={sortedWorkflows}
          columnDefinitions={[
            { id: "name", header: "Workflow", cell: renderName, width: 220 },
            { id: "status", header: LABELS.STATUS, cell: renderStatus, width: 120 },
            { id: "started", header: LABELS.STARTED, cell: renderStarted, width: 170 },
            { id: "ended", header: LABELS.COMPLETED, cell: renderEnded, width: 170 },
            { id: "duration", header: LABELS.DURATION, cell: renderDuration, width: 90 },
            { id: "actions", header: "", cell: renderActions },
          ]}
          empty={
            <Box textAlign="center" color="inherit" padding="l">
              <SpaceBetween size="m">
                <b>{LABELS.NO_WORKFLOWS_FOUND}</b>
                <Box variant="p" color="inherit">
                  {LABELS.NO_WORKFLOW_EXECUTIONS}
                </Box>
              </SpaceBetween>
            </Box>
          }
          variant="embedded"
          stripedRows
        />
      </Container>

      <WorkflowDetailModal
        workflow={selectedWorkflow}
        visible={!!selectedWorkflow}
        onDismiss={() => setSelectedWorkflow(null)}
        onRedrive={handleRedrive}
        timeMode={timeMode}
      />
    </>
  );
};
